import type { ColumnItem, ColumnTextBlock, ColumnsBlockProps } from '../types';
import { theme, sectionColors, buttonVars } from '../theme';
import { cardImage } from '../responsiveImage';
import { cellLinkKind } from './tableCellLinks';
import { RichText } from '../richText';
import { imageFocusPosition } from '../imageFocus';

/** A photo address the page may load: https, or a path on the site itself. Anything else shows no photo. */
export function photoSrc(url: unknown): string | null {
  if (typeof url !== 'string') return null;
  const u = url.trim();
  return /^https:\/\/\S+$/i.test(u) || /^\/(?!\/)\S*$/.test(u) ? u : null;
}

/** The most extra text blocks one column holds (the Structure form and BLT's writes keep to it too). */
export const COLUMN_TEXT_BLOCKS_MAX = 6;

/** A column's extra text blocks that have a heading or words, in order, up to the cap. */
export function columnTextBlocks(item: Pick<ColumnItem, 'textBlocks'>): ColumnTextBlock[] {
  if (!Array.isArray(item.textBlocks)) return [];
  return item.textBlocks
    .filter((b): b is ColumnTextBlock => Boolean(b) && typeof b === 'object'
      && Boolean((typeof b.heading === 'string' && b.heading.trim()) || (typeof b.body === 'string' && b.body.trim())))
    .slice(0, COLUMN_TEXT_BLOCKS_MAX);
}

function hasContent(item: ColumnItem): boolean {
  return Boolean(photoSrc(item.imageUrl) || item.heading?.trim() || item.body?.trim() || item.buttonLabel?.trim()
    || columnTextBlocks(item).length > 0);
}

/** One heading and its words: the column's own, and each of its extra text blocks, look the same. */
function HeadedText({ heading, body, headingSize, first }: { heading?: string; body?: string; headingSize: string; first: boolean }) {
  const h = typeof heading === 'string' ? heading.trim() : '';
  const b = typeof body === 'string' ? body.trim() : '';
  if (!h && !b) return null;
  const words = (
    <>
      {h ? (
        <h3 className={`${headingSize} font-semibold`} style={{ fontFamily: theme.headingFont }}>{heading}</h3>
      ) : null}
      {b ? (
        <RichText value={body as string} className="mt-3 space-y-3 leading-relaxed opacity-80" />
      ) : null}
    </>
  );
  // The first heading and words sit straight in the column (as a column always drew them); each
  // later block is set apart by a gap.
  return first ? words : <div data-columns-text="" className="mt-8">{words}</div>;
}

/**
 * Which layout the section draws. 'side' = each item is a row with its photo beside its words;
 * 'boxes' = the items stand side by side. Blank = 'side' for one item (the usual "photo next to
 * text"), 'boxes' for two or more.
 */
export function columnsLayout(props: Pick<ColumnsBlockProps, 'layout'>, count: number): 'side' | 'boxes' {
  if (props.layout === 'side' || props.layout === 'boxes') return props.layout;
  return count <= 1 ? 'side' : 'boxes';
}

/** Where a column's headings, text and button sit. Blank = left (as every column always drew). */
export function columnTextAlign(item: Pick<ColumnItem, 'textAlign'>): 'left' | 'center' | 'right' {
  return item.textAlign === 'center' || item.textAlign === 'right' ? item.textAlign : 'left';
}

const TEXT_ALIGN: Record<'center' | 'right', string> = { center: 'text-center', right: 'text-right' };

function Words({ item, headingSize }: { item: ColumnItem; headingSize: string }) {
  const label = item.buttonLabel?.trim();
  // A button only for an address a table cell could link to too (a page, a web address, a phone, an
  // email), or a spot on this page (#contact).
  const raw = item.buttonHref?.trim() ?? '';
  const href = raw && (cellLinkKind(raw) || /^#[A-Za-z][\w-]*$/.test(raw)) ? raw : null;
  const own = Boolean(item.heading?.trim() || item.body?.trim());
  // Left (blank) adds nothing, so a column that never set it draws exactly as before.
  const align = columnTextAlign(item);
  return (
    <div {...(align === 'left' ? {} : { 'data-columns-text-align': align, className: TEXT_ALIGN[align] })}>
      <HeadedText heading={item.heading} body={item.body} headingSize={headingSize} first />
      {columnTextBlocks(item).map((block, i) => (
        <HeadedText key={i} heading={block.heading} body={block.body} headingSize={headingSize} first={!own && i === 0} />
      ))}
      {label && href ? (
        <a href={href} className="blk-btn mt-5 inline-block rounded-md px-5 py-2.5 font-medium">{label}</a>
      ) : null}
    </div>
  );
}

type Shape = NonNullable<ColumnsBlockProps['imageShape']>;

/** The photos' frame. Blank = the whole photo, in either layout: a photo is never cut unless the owner picks a shape. */
export function columnsShape(props: Pick<ColumnsBlockProps, 'imageShape'>): Shape {
  const s = props.imageShape;
  if (s === 'whole' || s === 'landscape' || s === 'square' || s === 'portrait') return s;
  return 'whole';
}

const FRAME: Record<Exclude<Shape, 'whole'>, string> = {
  landscape: 'aspect-[4/3]',
  square: 'aspect-square',
  portrait: 'aspect-[3/4]',
};

/** The least and most a photo's size slider goes, as a percent of its column. */
export const COLUMN_IMAGE_WIDTH_MIN = 10;
export const COLUMN_IMAGE_WIDTH_MAX = 100;

/** A photo's width as a whole percent of its column, 10 to 100. Blank or not a number = 100 (as every photo always drew). */
export function columnImageWidth(item: Pick<ColumnItem, 'imageWidth'>): number {
  const w = item.imageWidth;
  if (typeof w !== 'number' || !Number.isFinite(w)) return COLUMN_IMAGE_WIDTH_MAX;
  return Math.min(COLUMN_IMAGE_WIDTH_MAX, Math.max(COLUMN_IMAGE_WIDTH_MIN, Math.round(w)));
}

/** Where a photo narrower than its column sits. Blank = center. */
export function columnImageAlign(item: Pick<ColumnItem, 'imageAlign'>): 'left' | 'center' | 'right' {
  return item.imageAlign === 'left' || item.imageAlign === 'right' ? item.imageAlign : 'center';
}

const ALIGN: Record<'left' | 'center' | 'right', string> = { left: 'mr-auto', center: 'mx-auto', right: 'ml-auto' };

function Photo({ item, shape, layout }: { item: ColumnItem; shape: Shape; layout: 'side' | 'boxes' }) {
  const src = photoSrc(item.imageUrl);
  if (!src) return null;
  const width = columnImageWidth(item);
  if (width >= COLUMN_IMAGE_WIDTH_MAX) return <PhotoImage src={src} item={item} shape={shape} layout={layout} align="center" />;
  // A smaller photo: its frame takes that share of the column's width and the photo scales inside
  // it, so its shape never changes (height follows width).
  const align = columnImageAlign(item);
  return (
    <div data-columns-photo-size={width} data-columns-photo-align={align} className={ALIGN[align]} style={{ width: `${width}%` }}>
      <PhotoImage src={src} item={item} shape={shape} layout={layout} align={align} sized />
    </div>
  );
}

function PhotoImage({ src, item, shape, layout, align, sized = false }: {
  src: string; item: ColumnItem; shape: Shape; layout: 'side' | 'boxes'; align: 'left' | 'center' | 'right';
  /** Inside a smaller frame (the size slider under 100%): the photo fills that frame's width, even a small one. */
  sized?: boolean;
}) {
  // 'whole' never crops: beside text the photo keeps its own shape (a tall headshot is capped in
  // height, not cut); in a box it fills the box's width. A set shape crops to the frame, and the
  // photo's focus point (top for a headshot) decides what stays in view.
  const position = imageFocusPosition(item.imageFocus);
  // A sized photo fills its frame (the owner picked that width), so it drops the height cap that
  // would otherwise shrink it below the width they chose; height follows width, shape kept.
  const className = shape === 'whole'
    ? layout === 'side' && !sized ? `${ALIGN[align]} block h-auto max-h-[36rem] w-auto max-w-full` : 'block h-auto w-full'
    : `w-full object-cover ${FRAME[shape]}`;
  return (
    <img
      src={cardImage(src)}
      alt={item.imageAlt ?? ''}
      loading="lazy"
      decoding="async"
      data-columns-photo={shape}
      className={`rounded-lg ${className}`}
      style={shape !== 'whole' && position ? { objectPosition: position } : undefined}
    />
  );
}

/**
 * Columns: a photo beside its words, or two to four boxes side by side, each with a photo, a
 * heading, words and a button. Phones always stack. Renders nothing when every item is empty.
 */
export function ColumnsBlock(props: ColumnsBlockProps) {
  const { heading, subheading } = props;
  const items = Array.isArray(props.items) ? props.items.filter((i) => i && typeof i === 'object' && hasContent(i)) : [];
  const colors = sectionColors(props, { bg: 'transparent', text: theme.text });
  if (items.length === 0) return null;
  const layout = columnsLayout(props, items.length);
  const firstRight = props.imageSide === 'right';
  const shape = columnsShape(props);

  return (
    <section className="px-6 py-12" style={{ ...colors, ...buttonVars(props), fontFamily: theme.bodyFont }}>
      <div className="mx-auto max-w-6xl">
        {heading ? (
          <h2 className="text-center text-2xl font-semibold" style={{ fontFamily: theme.headingFont }}>{heading}</h2>
        ) : null}
        {subheading ? <p className="mx-auto mt-2 max-w-2xl text-center text-sm opacity-70">{subheading}</p> : null}

        {layout === 'side' ? (
          <div className={`${heading || subheading ? 'mt-10' : ''} flex flex-col gap-12`}>
            {items.map((item, i) => {
              // Rows alternate sides, starting from the chosen one; on phones the photo is always on top.
              const right = i % 2 === 0 ? firstRight : !firstRight;
              const photo = photoSrc(item.imageUrl);
              return (
                <div key={i} data-columns-row="" className="grid items-center gap-8 md:grid-cols-2">
                  {photo ? (
                    <div className={right ? 'md:order-2' : undefined}>
                      <Photo item={item} shape={shape} layout="side" />
                    </div>
                  ) : null}
                  <div className={photo ? undefined : 'md:col-span-2'}>
                    <Words item={item} headingSize="text-2xl" />
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div
            // As many boxes per row as there are items, up to four; phones show one per row.
            className={`${heading || subheading ? 'mt-8' : ''} grid gap-8 ${
              items.length <= 2
                ? 'grid-cols-1 sm:grid-cols-2'
                : items.length === 3
                  ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
                  : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'}`}
          >
            {items.map((item, i) => (
              <div key={i} data-columns-box="" className="flex flex-col gap-4">
                <Photo item={item} shape={shape} layout="boxes" />
                <Words item={item} headingSize="text-lg" />
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
